// F-U (2026-10-05): the zone / exhibit forms' Back returns to the list as the
// user left it — filters, search, Status (all in the list URL) and scroll.
//
// sessionStorage: per-tab, survives the form round trip, gone with the tab.
// Every access is guarded — storage can be unavailable (private mode,
// blocked site data), in which case Back simply opens the plain list.

export type ListKind = "exhibits" | "zones";

const DEFAULT_URL: Record<ListKind, string> = {
  exhibits: "/staff/dashboard",
  zones: "/staff/zones",
};

const urlKey = (kind: ListKind) => `staff:list-url:${kind}`;
const scrollKey = (kind: ListKind) => `staff:list-scroll:${kind}`;

function read(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    /* storage unavailable — Back falls back to the plain list */
  }
}

function remove(key: string): void {
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

/** Called by the list page whenever its URL (filters / search) changes. */
export function rememberListUrl(kind: ListKind, url: string): void {
  write(urlKey(kind), url);
}

/** Where Back should go: the remembered list URL, else the plain list. Only
 *  a same-list path is honoured, so a stale value can't send Back elsewhere. */
export function listReturnUrl(kind: ListKind): string {
  const saved = read(urlKey(kind));
  const base = DEFAULT_URL[kind];
  return saved && (saved === base || saved.startsWith(`${base}?`))
    ? saved
    : base;
}

/** Called by the list page as it unmounts (user opened a row). */
export function saveListScroll(kind: ListKind, y: number): void {
  write(scrollKey(kind), String(Math.round(y)));
}

/** Read-and-clear the saved scroll offset (restored once per return). */
export function takeListScroll(kind: ListKind): number | null {
  const raw = read(scrollKey(kind));
  remove(scrollKey(kind));
  const y = raw === null ? NaN : Number(raw);
  return Number.isFinite(y) ? y : null;
}
